import { type ElementSelection, HexVersionId } from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { isEqual } from "lodash";

import { DataAppChromeColors } from "../../util/dataAppChromeTheme.js";
import { RootState } from "../store";

const MAX_RUNTIME_ERRORS = 20;

export type DataAppViewMode = "preview" | "code" | "select" | "edit";

export interface DataAppRuntimeError {
  message: string;
  stack?: string;
  timestamp: number;
}

/**
 * Chrome colors currently being applied to host UI (AppNavbar) next to the
 * data app. Populated from the runtime-sampled value postMessaged by the
 * iframe after render.
 */
export type DataAppThemeState = DataAppChromeColors;

export interface DataAppVersionState {
  files: Record<string, string> | null;
  entryPoint: string | null;
  lastModifiedAt: number | null;
  isLoading: boolean;
  runtimeErrors: DataAppRuntimeError[];
  /** Which file is open in the source panel of the App Builder tab. */
  selectedFile: string | null;
  /** Whether the App Builder tab shows the live preview or the source panel. */
  viewMode: DataAppViewMode;
  appTheme: DataAppThemeState | null;
  /** The chart element selected in edit mode (null = nothing selected). */
  elementSelection: ElementSelection | null;
  /**
   * Seeded component type currently under the pointer in select/edit mode
   * (e.g. "LineChart"). Null when not hovering a selectable chart.
   */
  elementHoverComponent: string | null;
  /** Frame-reported display name of the hovered region ("Legend"), if any. */
  elementHoverRegion: string | null;
  /** Ephemeral edit-panel preview bound for the iframe (never persisted). */
  editPreview: EditPreviewState | null;
}

/**
 * `values: null` means "clear the preview for `target`" — the shape mirrors
 * the HEX_EDIT_PREVIEW message, and a clear must still name its target.
 * `lastModifiedAt` is the files era the preview was computed against; the
 * reducer drops writes from a different era (see setEditPreview).
 */
export interface EditPreviewState {
  target: { component: string; instanceId?: string };
  values: Record<string, unknown> | null;
  lastModifiedAt: number | null;
}

type DataAppSliceState = Record<HexVersionId, DataAppVersionState | undefined>;

// `isLoading` defaults to `true` so that a freshly-synthesized entry — created
// e.g. by `setDataAppViewMode` / `setDataAppSelectedFile` before the actual
// load has fired — is treated as "not loaded yet" by `selectIsLoaded`. The
// flag is flipped to `false` only once we hear back from the server via
// `setDataAppState` or `setDataAppNotFound`.
const initialVersionState = (): DataAppVersionState => ({
  files: null,
  entryPoint: null,
  lastModifiedAt: null,
  isLoading: true,
  runtimeErrors: [],
  selectedFile: null,
  viewMode: "preview",
  appTheme: null,
  elementSelection: null,
  elementHoverComponent: null,
  elementHoverRegion: null,
  editPreview: null,
});

const dataAppSlice = createSlice({
  name: "dataApp",
  initialState: {} as DataAppSliceState,
  reducers: {
    setDataAppLoading: (
      state,
      action: PayloadAction<{ hexVersionId: HexVersionId }>,
    ) => {
      const { hexVersionId } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      state[hexVersionId] = { ...existing, isLoading: true };
    },
    setDataAppState: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        files: Record<string, string>;
        entryPoint: string;
        lastModifiedAt: number;
      }>,
    ) => {
      const { entryPoint, files, hexVersionId, lastModifiedAt } =
        action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      state[hexVersionId] = {
        ...existing,
        files,
        entryPoint,
        lastModifiedAt,
        isLoading: false,
        runtimeErrors: [],
        // A preview is meaningless against files it wasn't made for. Clearing
        // here (not in an effect) is what keeps the post-republish re-embed
        // from replaying a stale preview into the fresh iframe. The
        // selection deliberately survives (the panel must outlive Apply →
        // republish), but its embedded currentValues are a snapshot from
        // before the file change — panel consumers must re-derive current
        // values rather than trust them after this.
        editPreview: null,
      };
    },
    setDataAppTheme: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        theme: DataAppThemeState | null;
      }>,
    ) => {
      const { hexVersionId, theme } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      state[hexVersionId] = { ...existing, appTheme: theme };
    },
    setDataAppNotFound: (
      state,
      action: PayloadAction<{ hexVersionId: HexVersionId }>,
    ) => {
      const { hexVersionId } = action.payload;
      state[hexVersionId] = { ...initialVersionState(), isLoading: false };
    },
    addRuntimeError: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        error: DataAppRuntimeError;
      }>,
    ) => {
      const { error, hexVersionId } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      const errors = [...existing.runtimeErrors, error].slice(
        -MAX_RUNTIME_ERRORS,
      );
      state[hexVersionId] = { ...existing, runtimeErrors: errors };
    },
    clearRuntimeErrors: (
      state,
      action: PayloadAction<{ hexVersionId: HexVersionId }>,
    ) => {
      const { hexVersionId } = action.payload;
      const existing = state[hexVersionId];
      if (existing != null) {
        state[hexVersionId] = { ...existing, runtimeErrors: [] };
      }
    },
    setDataAppViewMode: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        viewMode: DataAppViewMode;
      }>,
    ) => {
      const { hexVersionId, viewMode } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      const inSelectionMode = viewMode === "select" || viewMode === "edit";
      state[hexVersionId] = {
        ...existing,
        viewMode,
        elementSelection: inSelectionMode ? existing.elementSelection : null,
        elementHoverComponent: inSelectionMode
          ? existing.elementHoverComponent
          : null,
        elementHoverRegion: inSelectionMode
          ? existing.elementHoverRegion
          : null,
      };
    },
    /**
     * Backs the selection flow out one level: a selection (locked-in or
     * provisional) → select mode with nothing selected, empty select mode →
     * preview. Shared by the edit panel Close button, host Escape, and
     * Escape relayed from the iframe.
     */
    dismissElementSelection: (
      state,
      action: PayloadAction<{ hexVersionId: HexVersionId }>,
    ) => {
      const { hexVersionId } = action.payload;
      const existing = state[hexVersionId];
      if (existing == null) return;
      if (
        (existing.viewMode === "edit" || existing.viewMode === "select") &&
        existing.elementSelection != null
      ) {
        state[hexVersionId] = {
          ...existing,
          viewMode: "select",
          elementSelection: null,
        };
        return;
      }
      if (existing.viewMode === "select" || existing.viewMode === "edit") {
        state[hexVersionId] = {
          ...existing,
          viewMode: "preview",
          elementSelection: null,
          elementHoverComponent: null,
          elementHoverRegion: null,
        };
      }
    },
    setElementSelection: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        selection: ElementSelection | null;
      }>,
    ) => {
      const { hexVersionId, selection } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      // Bail when the identity, geometry, and reported values are unchanged,
      // so an app looping selection posts can't churn every subscriber with
      // fresh objects. mountId, currentValues, and codeDefinedKeys must
      // participate: Apply folds edits into currentValues only, the
      // post-republish reconcile changes mountId only, and provenance may
      // change independently of both.
      const previous = existing.elementSelection;
      if (
        selection != null &&
        previous != null &&
        previous.kind === selection.kind &&
        previous.rect.x === selection.rect.x &&
        previous.rect.y === selection.rect.y &&
        previous.rect.width === selection.rect.width &&
        previous.rect.height === selection.rect.height &&
        (selection.kind === "text"
          ? previous.kind === "text" &&
            previous.tagId === selection.tagId &&
            previous.elementType === selection.elementType &&
            previous.label === selection.label
          : previous.kind === "chart" &&
            previous.component === selection.component &&
            previous.mountId === selection.mountId &&
            previous.instanceId === selection.instanceId &&
            previous.region === selection.region &&
            previous.seriesKey === selection.seriesKey &&
            isEqual(previous.currentValues, selection.currentValues) &&
            isEqual(previous.codeDefinedKeys, selection.codeDefinedKeys))
      ) {
        return;
      }
      state[hexVersionId] = {
        ...existing,
        elementSelection: selection,
      };
    },
    setElementHoverComponent: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        component: string | null;
        region?: string | null;
      }>,
    ) => {
      const { component, hexVersionId, region = null } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      if (
        existing.elementHoverComponent === component &&
        existing.elementHoverRegion === region
      ) {
        return;
      }
      state[hexVersionId] = {
        ...existing,
        elementHoverComponent: component,
        elementHoverRegion: region,
      };
    },
    setEditPreview: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        preview: EditPreviewState | null;
      }>,
    ) => {
      const { hexVersionId, preview } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      // A panel debounce timer can fire in the gap between setDataAppState
      // (which cleared editPreview for the new files) and the panel's own
      // invalidation effect. Dropping writes stamped with a different files
      // era keeps that late write from replaying into the fresh iframe.
      if (
        preview != null &&
        preview.lastModifiedAt !== existing.lastModifiedAt
      ) {
        return;
      }
      state[hexVersionId] = { ...existing, editPreview: preview };
    },
    setDataAppSelectedFile: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        selectedFile: string | null;
      }>,
    ) => {
      const { hexVersionId, selectedFile } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      state[hexVersionId] = { ...existing, selectedFile };
    },
  },
});

export const {
  addRuntimeError,
  clearRuntimeErrors,
  dismissElementSelection,
  setDataAppLoading,
  setDataAppNotFound,
  setDataAppSelectedFile,
  setDataAppState,
  setDataAppTheme,
  setDataAppViewMode,
  setEditPreview,
  setElementHoverComponent,
  setElementSelection,
} = dataAppSlice.actions;

export const dataAppReducer = dataAppSlice.reducer;

const selectVersionState = (
  state: RootState,
  hexVersionId: HexVersionId,
): DataAppVersionState | undefined => state.dataApp[hexVersionId];

export const dataAppSelectors = {
  selectFiles: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.files ?? null,

  /**
   * Returns whether a specific path exists in the loaded VFS. `null` means
   * the VFS hasn't been loaded yet (callers should typically treat this as
   * "assume present" so we don't flag rows as missing before we know).
   *
   * Designed for `useSelector` subscriptions in per-row components: it
   * returns a scalar `boolean | null`, so the default referential-equality
   * check is sufficient and rows only re-render when their own path's
   * existence flips. Prefer this over reading `selectFiles` and computing
   * existence at render time, which causes a re-render on every unrelated
   * VFS write.
   */
  selectFileExists: (
    state: RootState,
    hexVersionId: HexVersionId,
    path: string,
  ): boolean | null => {
    const files = selectVersionState(state, hexVersionId)?.files;
    if (files == null) {
      return null;
    }
    return Object.hasOwn(files, path);
  },

  // True once we've heard back from the server (either `setDataAppState` or
  // `setDataAppNotFound`). Before that, `selectFiles` returns `null` both for
  // the pre-load case and the genuinely-empty case, and callers that need to
  // distinguish "not loaded yet" from "confirmed no files" should use this.
  selectIsLoaded: (state: RootState, hexVersionId: HexVersionId) => {
    const versionState = selectVersionState(state, hexVersionId);
    return versionState != null && !versionState.isLoading;
  },

  selectEntryPoint: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.entryPoint ?? null,

  selectIsLoading: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.isLoading ?? false,

  selectRuntimeErrors: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.runtimeErrors ?? [],

  selectHasApp: (state: RootState, hexVersionId: HexVersionId) =>
    (selectVersionState(state, hexVersionId)?.files ?? null) != null,

  selectLastModifiedAt: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.lastModifiedAt ?? null,

  selectAppTheme: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.appTheme ?? null,

  selectSelectedFile: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.selectedFile ?? null,

  selectViewMode: (
    state: RootState,
    hexVersionId: HexVersionId,
  ): DataAppViewMode =>
    selectVersionState(state, hexVersionId)?.viewMode ?? "preview",

  selectElementSelection: (
    state: RootState,
    hexVersionId: HexVersionId,
  ): ElementSelection | null =>
    selectVersionState(state, hexVersionId)?.elementSelection ?? null,

  selectElementHoverComponent: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.elementHoverComponent ?? null,

  selectElementHoverRegion: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.elementHoverRegion ?? null,

  selectEditPreview: (
    state: RootState,
    hexVersionId: HexVersionId,
  ): EditPreviewState | null =>
    selectVersionState(state, hexVersionId)?.editPreview ?? null,
};
